import { useCallback, useState } from 'react';

import { usePresignAttachments } from './api';
import { AttachmentRef, AttachmentRole } from './types';

export interface PendingAttachment {
    id: string;
    fileName: string;
    sizeBytes: number;
    status: 'uploading' | 'done' | 'error';
    key?: string;
}

/** Presigns and uploads dropped files straight to S3, tracking per-file
 * status so the caller can block submission until uploads settle. */
export function useAttachmentUpload(role: AttachmentRole = 'attachment') {
    const presign = usePresignAttachments();
    const [attachments, setAttachments] = useState<PendingAttachment[]>([]);

    const updateStatus = useCallback(
        (id: string, patch: Partial<PendingAttachment>) => {
            setAttachments((prev) =>
                prev.map((a) => (a.id === id ? { ...a, ...patch } : a)),
            );
        },
        [],
    );

    const addFiles = useCallback(
        (files: File[]) => {
            if (files.length === 0) return;

            const items = files.map((file) => ({
                id: crypto.randomUUID(),
                file,
                fileName: file.name,
                sizeBytes: file.size,
            }));

            setAttachments((prev) => [
                ...prev,
                ...items.map(({ id, fileName, sizeBytes }) => ({
                    id,
                    fileName,
                    sizeBytes,
                    status: 'uploading' as const,
                })),
            ]);

            presign
                .mutateAsync(
                    items.map(({ file }) => ({
                        fileName: file.name,
                        contentType: file.type || 'application/octet-stream',
                    })),
                )
                .then((presigned) => {
                    items.forEach((item, index) => {
                        const target = presigned[index];
                        fetch(target.uploadUrl, {
                            method: 'PUT',
                            headers: {
                                'Content-Type':
                                    item.file.type || 'application/octet-stream',
                            },
                            body: item.file,
                        })
                            .then((res) => {
                                if (!res.ok) throw new Error('Upload failed');
                                updateStatus(item.id, {
                                    status: 'done',
                                    key: target.key,
                                });
                            })
                            .catch(() => updateStatus(item.id, { status: 'error' }));
                    });
                })
                .catch(() => {
                    items.forEach((item) =>
                        updateStatus(item.id, { status: 'error' }),
                    );
                });
        },
        [presign, updateStatus],
    );

    const removeAttachment = useCallback((id: string) => {
        setAttachments((prev) => prev.filter((a) => a.id !== id));
    }, []);

    const reset = useCallback(() => setAttachments([]), []);

    const isUploading = attachments.some((a) => a.status === 'uploading');
    const readyRefs: AttachmentRef[] = attachments
        .filter((a) => a.status === 'done')
        .map((a) => ({ key: a.key as string, fileName: a.fileName, role }));

    return {
        attachments,
        addFiles,
        removeAttachment,
        reset,
        isUploading,
        readyRefs,
    };
}
